import { Button, IconButton } from '@dixa/lib-shared-habla';
import { useAtomValue } from 'jotai';
import { useEditableProps } from 'lib-knowledge-document-structure/plate/common';
import { useMyEditorState } from 'lib-knowledge-document-structure/plateHooks';
import { LanguageContext } from 'lib-knowledge-management-common/contexts/language';
import { useTypedParams } from 'lib-knowledge-utils/hooks/useTypedParams';
import { routeGenerator } from 'lib-knowledge-utils/routes';
import { featureFlagsAtom } from 'lib-knowledge-utils/stores/featureFlags';
import { ampli } from 'lib-shared-tracking';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { ActionsDropdown } from './actionsDropdown';
import { PublishingDropdown } from './publishingDropdown';

type HeaderControlsProps = {
  seoSidebarOpen: boolean;
  onSeoSidebarToggle: () => void;
};

export const HeaderControls = ({
  seoSidebarOpen,
  onSeoSidebarToggle,
}: HeaderControlsProps): JSX.Element => {
  const editor = useMyEditorState();
  const { readOnly } = useEditableProps();
  const { articleId } = useTypedParams(['articleId']);
  const { currentLanguage } = useContext(LanguageContext);
  const navigate = useNavigate();
  const { featureFlags } = useAtomValue(featureFlagsAtom);

  if (readOnly) return <></>;

  const canRedo = editor.history.redos.length > 0;
  const canUndo = editor.history.undos.length > 0;

  const goToVersionHistory = () => {
    ampli.knowledgeArticleVersionHistoryClicked();
    navigate(
      routeGenerator.articleVersionHistory({
        articleId,
        languageId: currentLanguage ?? undefined,
      }),
    );
  };

  return (
    <>
      <IconButton
        variant="tertiary"
        icon="undo"
        disabled={!canUndo}
        onClick={editor.undo}
      />
      <IconButton
        variant="tertiary"
        icon="redo"
        disabled={!canRedo}
        onClick={editor.redo}
      />

      <ActionsDropdown />
      {featureFlags.seoMeta && (
        <Button
          variant="secondary"
          active={seoSidebarOpen}
          onClick={onSeoSidebarToggle}
        >
          SEO
        </Button>
      )}
      <Button variant="secondary" onClick={goToVersionHistory}>
        Version History
      </Button>
      <PublishingDropdown />
    </>
  );
};
